import type { ICartItemDetail } from '@/common/interfaces/cart.interface';

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*?>/i;

const splitPlainTextDescription = (text: string): string[] => {
  const normalized = text.trim();

  if (!normalized) {
    return [];
  }

  const byLine = normalized
    .split(/\r?\n+|[•·]|\s*;\s*/)
    .map((entry) => entry.trim())
    .filter(Boolean);

  if (byLine.length > 1) {
    return byLine;
  }

  const byComma = normalized
    .split(/\s*,\s*/)
    .map((entry) => entry.trim())
    .filter(Boolean);

  if (byComma.length > 1) {
    return byComma;
  }

  return [normalized];
};

const parseHtmlDescriptionBullets = (description: string): string[] => {
  const bullets: string[] = [];
  const htmlStartIndex = description.search(/</);

  if (htmlStartIndex > 0) {
    bullets.push(...splitPlainTextDescription(description.slice(0, htmlStartIndex)));
    description = description.slice(htmlStartIndex);
  }

  if (!description.trim()) {
    return bullets;
  }

  const doc = new DOMParser().parseFromString(`<div>${description}</div>`, 'text/html');
  const container = doc.body.firstElementChild;

  if (!container) {
    return bullets;
  }

  const extractBulletsFromNode = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();

      if (text) {
        bullets.push(
          ...text
            .split(/\r?\n+/)
            .map((entry) => entry.trim())
            .filter(Boolean)
        );
      }

      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const element = node as Element;
    const tagName = element.tagName.toLowerCase();

    if (tagName === 'li' || tagName === 'p') {
      const text = element.textContent?.trim();

      if (text) {
        bullets.push(text);
      }

      return;
    }

    element.childNodes.forEach(extractBulletsFromNode);
  };

  container.childNodes.forEach(extractBulletsFromNode);

  return bullets;
};

/** Product info UI copy persisted as order item product_description on update. */
export const resolveStoreReviewProductInfo = (item: ICartItemDetail): string =>
  item.productDescription?.trim() ?? '';

/** Split product_description into bullet lines for the Product Info list UI. */
export const parseStoreReviewProductInfoBullets = (productDescription?: string): string[] => {
  const normalized = productDescription?.trim();

  if (!normalized) {
    return [];
  }

  if (HTML_TAG_PATTERN.test(normalized)) {
    const htmlBullets = parseHtmlDescriptionBullets(normalized);

    if (htmlBullets.length > 0) {
      return htmlBullets;
    }
  }

  return splitPlainTextDescription(normalized);
};
